import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Lock, Unlock } from 'lucide-react';
import { useGoogleMaps } from '@planner/bundles/main/hooks/useGoogleMaps';
import { Modal } from '@portal/portal_registration/components/Modal';
import { Switch } from '@portal/components/common/Switch';

// Define the shape of the location data the modal works with
export interface LocationData {
  venueName: string;
  streetAddress: string;
  streetAddress2?: string;
  city: string;
  stateRegion: string;
  postalCode: string;
  country: string;
}

interface LocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: LocationData; // Initial location data passed from parent
  onSave: (updatedLocation: LocationData) => void; // Callback to send updated data back
}

export const LocationModal = ({
  isOpen,
  onClose,
  location: initialLocation,
  onSave,
}: LocationModalProps) => {
  const [modalLocation, setModalLocation] = useState<LocationData>(initialLocation);
  const [errors, setErrors] = useState<Partial<Record<keyof LocationData, string>>>({});
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [isManualOverride, setIsManualOverride] = useState(false);
  const [wasGoogleFilled, setWasGoogleFilled] = useState(false);

  // Refs for map and autocomplete
  const mapRef = useRef<HTMLDivElement>(null);
  const venueInputRef = useRef<HTMLInputElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const autocompleteRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
  const markerRef = useRef<google.maps.Circle | null>(null); // Using Circle as marker

  // Load Google Maps API
  const mapsStatus = useGoogleMaps(['places', 'geocoding']);

  // --- Form Handlers (Moved Up) ---
  const handleChange = useCallback((field: keyof LocationData, value: string) => {
    setModalLocation(prev => {
      const updated = { ...prev, [field]: value };

      // If manually editing address fields (not venue name) and was google filled, mark as no longer Google filled
      const addressFields: (keyof LocationData)[] = ['streetAddress', 'streetAddress2', 'city', 'stateRegion', 'postalCode', 'country'];
      if (wasGoogleFilled && addressFields.includes(field)) {
        setWasGoogleFilled(false); // Reset since user is manually editing
      }

      return updated;
    });

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors, wasGoogleFilled]); // Dependencies: errors, wasGoogleFilled

  const fillInAddress = useCallback((place: google.maps.places.PlaceResult) => {
     const components: Partial<LocationData> = {
        // venueName will be handled separately based on logic below
        streetAddress: '',
        streetAddress2: '',
        city: '',
        stateRegion: '',
        postalCode: '',
        country: '',
     };

     let streetNumber = '';
     let route = '';

     place.address_components?.forEach(component => {
        // Check all types, not just the first one
        const types = component.types;

        // Use the first matching type from our priority list
        if (types.includes('street_number')) {
            streetNumber = component.long_name;
        } else if (types.includes('route')) {
            route = component.long_name;
        } else if (!components.city && (types.includes('locality') || types.includes('postal_town') ||
                   types.includes('sublocality_level_1') || types.includes('administrative_area_level_2'))) {
            components.city = component.long_name;
        } else if (types.includes('administrative_area_level_1')) {
            components.stateRegion = component.short_name;
        } else if (types.includes('postal_code')) {
            components.postalCode = component.long_name;
        } else if (types.includes('country')) {
            components.country = component.long_name;
        }
     });

     components.streetAddress = `${streetNumber} ${route}`.trim();

     // Update state using functional update to get the latest venueName
     setModalLocation(prev => {
        let finalVenueName = prev.venueName; // Start with current name from previous state

        // Prioritize the name from the Google Place result if it exists
        if (place.name) {
            finalVenueName = place.name;
        }
        // Fallback: If no place name was provided by Google and the venue name is currently empty,
        // use the street address if available.
        else if (components.streetAddress && !finalVenueName) {
             finalVenueName = components.streetAddress;
        }
        // Otherwise, keep the existing venueName (either user-typed or previously set)


        return {
            ...prev,
            ...components, // Apply address components
            venueName: finalVenueName // Apply decided venue name
        };
     });

     setErrors({}); // Clear errors after autocomplete fill
     setWasGoogleFilled(true); // Mark that data came from Google
     setIsManualOverride(false); // Disable manual override when selecting from Google
  }, []); // No dependencies needed if only using setModalLocation functional update

  // --- Address Handling & Map Update (Moved Up) ---
  const isAddressComplete = useCallback((loc: LocationData): boolean => {
    // Require street, city, and state/region for map preview
    return !!(loc.streetAddress?.trim() && loc.city?.trim() && loc.stateRegion?.trim());
  }, []);

  const buildAddressString = useCallback((loc: LocationData): string => {
    // Use venue name if street address is missing, otherwise prioritize street address
    const firstPart = loc.streetAddress?.trim() || loc.venueName?.trim() || '';
    const addressParts = [firstPart];

    // Add street address 2 if present
    if (loc.streetAddress2?.trim()) {
      addressParts.push(loc.streetAddress2.trim());
    }

    return [
      ...addressParts,
      loc.city?.trim(),
      loc.stateRegion?.trim(),
      loc.postalCode?.trim(),
      loc.country?.trim(),
    ]
      .filter(Boolean) // Remove empty parts
      .join(', ');
  }, []);

  const updateMap = useCallback((address: string) => {
    if (!geocoderRef.current || !mapRef.current) return;

    geocoderRef.current.geocode({ address: address }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
        const location = results[0].geometry.location;
        setIsMapVisible(true);

        if (!mapInstanceRef.current && mapRef.current) { // Ensure mapRef.current exists
          // Initialize map
          mapInstanceRef.current = new google.maps.Map(mapRef.current, { // Removed ! assertion
            center: location,
            zoom: 15,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            disableDefaultUI: true,
            draggable: false,
            zoomControl: false,
            scrollwheel: false,
            disableDoubleClickZoom: true,
          });
          // Initialize marker (circle)
          markerRef.current = new google.maps.Circle({
            strokeColor: "#DB4437",
            strokeOpacity: 1.0,
            strokeWeight: 2,
            fillColor: "#DB4437",
            fillOpacity: 1.0,
            map: mapInstanceRef.current,
            center: location,
            radius: 8,
          });
        } else if (mapInstanceRef.current) { // Ensure mapInstanceRef.current exists
          // Update existing map center and marker position
          mapInstanceRef.current.setCenter(location);
          if (markerRef.current) {
            markerRef.current.setCenter(location);
          }
        }
        // Ensure map resizes correctly after becoming visible
        setTimeout(() => {
          if (mapInstanceRef.current) {
             google.maps.event.trigger(mapInstanceRef.current, 'resize');
          }
        }, 100);

      } else {
        console.warn(`Geocode was not successful for the following reason: ${status}`);
        setIsMapVisible(false);
        // Optionally show geocode error status to user
      }
    });
  }, []); // Dependencies: geocoderRef, mapRef (refs don't need to be listed)

  // --- Map and Geocoder Initialization ---
  useEffect(() => {
    if (mapsStatus === 'ready' && !geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }
  }, [mapsStatus]);

  // --- Autocomplete Initialization ---
  useEffect(() => {
    // Ensure the modal is open, the API is ready, the input exists, and autocomplete isn't already initialized
    if (isOpen && mapsStatus === 'ready' && venueInputRef.current && !autocompleteRef.current) {
      const autocomplete = new google.maps.places.Autocomplete(venueInputRef.current, {
        fields: ['address_components', 'geometry', 'name', 'formatted_address'],
        types: ['establishment', 'geocode'], // Allow both business names and addresses
      });

      // Store the listener instance to remove it later
      const placeChangedListener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.address_components) {
          fillInAddress(place);
          // Note: Venue name update logic is now inside fillInAddress
        } else {
          console.warn("Autocomplete place has no address components:", place);
          // If only name is available, handle it within fillInAddress or handleChange if needed
          if(place.name) {
             // Decide if just the name should update the venueName field
             // handleChange('venueName', place.name); // Example if you want this behavior
          }
        }
      });

      autocompleteRef.current = autocomplete; // Store the autocomplete instance

      // Return a cleanup function
      return () => {
        if (autocompleteRef.current) {
          google.maps.event.removeListener(placeChangedListener);
          google.maps.event.clearInstanceListeners(autocompleteRef.current);
          const pacContainers = document.querySelectorAll('.pac-container');
          pacContainers.forEach(container => container.remove());
          autocompleteRef.current = null;
          console.log("Autocomplete cleaned up.");
        }
      };
    }
    // Manual cleanup if modal closes while autocomplete exists
    else if (!isOpen && autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        const pacContainers = document.querySelectorAll('.pac-container');
        pacContainers.forEach(container => container.remove());
        autocompleteRef.current = null;
        console.log("Autocomplete manually cleaned up on close.");
    }

  }, [isOpen, mapsStatus, fillInAddress, handleChange]); // Dependencies: isOpen, mapsStatus, fillInAddress, handleChange. venueInputRef.current is not needed here.


  // Effect to update map when address changes and is complete
  useEffect(() => {
    if (isOpen && mapsStatus === 'ready' && isAddressComplete(modalLocation)) {
      const addressString = buildAddressString(modalLocation);
      updateMap(addressString);
    } else if (isOpen) { // Only hide if open but address incomplete
      setIsMapVisible(false); // Hide map if address is incomplete
    }
  }, [modalLocation, isOpen, mapsStatus]); // Removed function dependencies to prevent re-runs


  // Reset internal state and hide map when the modal opens or initial location changes
  useEffect(() => {
    if (isOpen) {
      // Only reset state when modal first opens, not on subsequent dependency changes
      // This prevents clearing Google-filled data during autocomplete selection
      setModalLocation(prev => {
        // If we already have Google-filled data, preserve it
        if (wasGoogleFilled) {
          return prev;
        }
        // Otherwise, reset to initialLocation but preserve venueName if it exists
        return {
          ...initialLocation,
          venueName: initialLocation.venueName || prev.venueName
        };
      });

      // Only reset these states if not already Google-filled
      if (!wasGoogleFilled) {
        setErrors({});
        setIsManualOverride(false);
      }

      // Check initial address completeness on open
      if (mapsStatus === 'ready' && isAddressComplete(initialLocation)) {
         const addressString = buildAddressString(initialLocation);
         // Delay map update slightly to ensure modal is rendered and map div exists
         setTimeout(() => updateMap(addressString), 0);
      } else {
         setIsMapVisible(false);
      }
    } else {
        setIsMapVisible(false); // Ensure map is hidden when modal closes
        // Reset map instance when closing to avoid issues on reopen
        mapInstanceRef.current = null;
        markerRef.current = null;
        // Reset Google-filled status when modal closes
        setWasGoogleFilled(false);
    }
  }, [initialLocation, isOpen, mapsStatus]); // Removed wasGoogleFilled to prevent race condition


  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof LocationData, string>> = {};
    if (!modalLocation.venueName?.trim()) {
      newErrors.venueName = 'Venue name is required';
    }
    // Add other validation rules if needed (e.g., country)
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(modalLocation);
    }
  };

  // --- Render ---

  return (
    <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Location"
        size="lg"
    >
        {/* Modal Body - Form Fields */}
        <div className="space-y-4">
            {/* Venue Name (with Autocomplete) */}
            <div>
            <label htmlFor="venueName" className="block text-sm font-medium text-gray-700">
                Location Name or Address <span className="text-red-500">*</span>
            </label>
            <input
                ref={venueInputRef} // Attach ref for autocomplete
                id="venueName"
                type="text"
                value={modalLocation.venueName}
                onChange={(e) => handleChange('venueName', e.target.value)}
                placeholder="Start typing venue name or address..."
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${errors.venueName ? 'border-red-500' : 'border-gray-300'}`}
                disabled={mapsStatus !== 'ready'} // Disable input until maps API is ready
            />
            {errors.venueName && <p className="mt-1 text-sm text-red-500">{errors.venueName}</p>}
                {mapsStatus === 'loading' && <p className="mt-1 text-xs text-gray-500">Loading address suggestions...</p>}
                {mapsStatus === 'error' && <p className="mt-1 text-xs text-red-500">Could not load address suggestions.</p>}
            </div>

            {/* Manual Override Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
              <div className="flex items-center space-x-2">
                {isManualOverride ? <Unlock className="h-4 w-4 text-blue-600" /> : <Lock className="h-4 w-4 text-gray-400" />}
                <Switch
                  checked={isManualOverride}
                  onChange={(checked) => {
                    setIsManualOverride(checked);
                    if (checked && wasGoogleFilled) {
                      // When enabling manual override, keep the data but mark as not Google-filled
                      setWasGoogleFilled(false);
                    }
                  }}
                  label="Enter address manually"
                />
              </div>
              {isManualOverride && (
                <p className="text-xs text-gray-500">Address fields are now editable</p>
              )}
            </div>

            {/* Street Address */}
            <div>
            <label htmlFor="streetAddress" className="block text-sm font-medium text-gray-700">
                Street Address
            </label>
            <input
                id="streetAddress"
                type="text"
                value={modalLocation.streetAddress}
                onChange={(e) => handleChange('streetAddress', e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${!isManualOverride ? 'bg-gray-50' : ''}`}
                readOnly={!isManualOverride}
            />
            </div>

            {/* Street Address Line 2 */}
            <div>
            <label htmlFor="streetAddress2" className="block text-sm font-medium text-gray-700">
                Street Address Line 2 <span className="text-gray-400 text-xs">(Suite, Apt, etc.)</span>
            </label>
            <input
                id="streetAddress2"
                type="text"
                value={modalLocation.streetAddress2 || ''}
                onChange={(e) => handleChange('streetAddress2', e.target.value)}
                placeholder="Suite C, Apt 4B, Floor 2, etc."
                className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${!isManualOverride ? 'bg-gray-50' : ''}`}
                readOnly={!isManualOverride}
            />
            </div>

            {/* City & State/Region */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                City
                </label>
                <input
                id="city"
                type="text"
                value={modalLocation.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${!isManualOverride ? 'bg-gray-50' : ''}`}
                readOnly={!isManualOverride}
                />
            </div>
            <div>
                <label htmlFor="stateRegion" className="block text-sm font-medium text-gray-700">
                State/Region
                </label>
                <input
                id="stateRegion"
                type="text"
                value={modalLocation.stateRegion}
                onChange={(e) => handleChange('stateRegion', e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${!isManualOverride ? 'bg-gray-50' : ''}`}
                readOnly={!isManualOverride}
                />
            </div>
            </div>

            {/* Postal Code & Country */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700">
                Postal Code
                </label>
                <input
                id="postalCode"
                type="text"
                value={modalLocation.postalCode}
                onChange={(e) => handleChange('postalCode', e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${!isManualOverride ? 'bg-gray-50' : ''}`}
                readOnly={!isManualOverride}
                />
            </div>
            <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                Country
                </label>
                <input
                id="country"
                type="text"
                value={modalLocation.country}
                onChange={(e) => handleChange('country', e.target.value)}
                className={`mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${!isManualOverride ? 'bg-gray-50' : ''}`}
                readOnly={!isManualOverride}
                />
            </div>
            </div>

            {/* Map Preview Area */}
            <div className={`map-preview transition-all duration-300 ease-in-out overflow-hidden ${isMapVisible ? 'h-48 opacity-100 mt-4' : 'h-0 opacity-0 mt-0'}`}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                Map Preview
                </label>
                <div
                ref={mapRef}
                id="location-map-preview"
                className="w-full h-full bg-gray-200 rounded" // Style the map container
                >
                {mapsStatus === 'loading' && !isMapVisible && <div className="flex items-center justify-center h-full text-gray-500">Loading Map...</div>}
                {mapsStatus === 'error' && !isMapVisible && <div className="flex items-center justify-center h-full text-red-500">Error loading map</div>}
                {/* Map instance will render here */}
                </div>
            </div>

        </div>

        {/* Modal Footer - Actions */}
        <div className="flex justify-end gap-3 mt-6">
            <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
            Cancel
            </button>
            <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
            Add Location
            </button>
        </div>
    </Modal>
  );
};